import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send,
  User,
  DollarSign,
  Calendar,
  CreditCard,
  Building,
  Globe,
  Clock,
  Check,
  ArrowRight,
  Users,
  Smartphone,
  Mail,
  Phone,
  AlertCircle,
  ChevronRight,
  History,
  Menu,
  Loader2,
  RefreshCw,
  Plus,
  CheckCircle,
  X,
  Download,
  ArrowUpRight,
  ArrowDownLeft,
  Eye,
  Copy
} from 'lucide-react'
import { TransfersAPI, AccountsAPI, extractArray } from '../services/api'

const getTransferMethods = (t) => [
  {
    id: 'internal',
    name: t('methods.internal.title'),
    description: t('methods.internal.description'),
    icon: CreditCard,
    fee: t('fees.free'),
    time: t('times.instant'),
    color: 'bg-blue-50 text-blue-600',
    available: true,
    category: 'internal'
  },
  {
    id: 'ach',
    name: t('methods.ach.title'),
    description: t('methods.ach.description'),
    icon: Building,
    fee: t('fees.free'),
    time: t('times.business1to3'),
    color: 'bg-slate-100 text-slate-400',
    available: false,
    comingSoon: true,
    category: 'external'
  },
  {
    id: 'wire',
    name: t('methods.wire.title'),
    description: t('methods.wire.description'),
    icon: Globe,
    fee: '$25',
    time: t('times.sameDay'),
    color: 'bg-slate-100 text-slate-400',
    available: false,
    comingSoon: true,
    category: 'external'
  },
  {
    id: 'zelle',
    name: t('methods.zelle.title'),
    description: t('methods.zelle.description'),
    icon: Users,
    fee: t('fees.free'),
    time: t('times.instant'),
    color: 'bg-slate-100 text-slate-400',
    available: false,
    comingSoon: true,
    category: 'external'
  }
]

export default function TransferPage({ onMenuClick }) {
  const { t, i18n } = useTranslation(['transfers', 'common'])
  const transferMethods = getTransferMethods(t)

  const [activeTab, setActiveTab] = useState('new')
  const [transferMethod, setTransferMethod] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [recipients, setRecipients] = useState([])
  const [transfers, setTransfers] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [formData, setFormData] = useState({
    fromAccountId: '',
    toAccountId: '',
    recipientId: '',
    amount: '',
    description: '',
    memo: '',
    scheduledDate: ''
  })

  // Confirmation and Receipt states
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showReceipt, setShowReceipt] = useState(false)
  const [receiptData, setReceiptData] = useState(null)

  // Transfer detail modal state
  const [selectedTransfer, setSelectedTransfer] = useState(null)
  const [showTransferDetail, setShowTransferDetail] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [accountsRes, recipientsRes, transfersRes] = await Promise.all([
        AccountsAPI.getAll(),
        TransfersAPI.getRecipients(),
        TransfersAPI.getAll({ limit: 10 })
      ])

      if (accountsRes.success) {
        const accountData = extractArray(accountsRes, 'accounts')
        setAccounts(accountData)
        if (accountData.length > 0) {
          setFormData(prev => ({ ...prev, fromAccountId: accountData[0].id }))
        }
      }
      if (recipientsRes.success) {
        const recipientData = extractArray(recipientsRes)
        setRecipients(recipientData)
      }
      if (transfersRes.success) {
        const transferData = extractArray(transfersRes)
        setTransfers(transferData)
      }
    } catch (err) {
      console.error('Error fetching data:', err)
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }))
    setError(null)
  }

  // Validate form and show confirmation
  const handleReviewTransfer = () => {
    setError(null)

    if (!formData.fromAccountId) {
      setError(t('validation.selectSourceAccount'))
      return
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError(t('validation.enterValidAmount'))
      return
    }

    if (transferMethod === 'internal') {
      if (!formData.toAccountId) {
        setError(t('validation.selectDestinationAccount'))
        return
      }
    } else {
      if (!formData.recipientId) {
        setError(t('validation.selectRecipient'))
        return
      }
    }

    // Check sufficient funds
    const fromAccount = getSelectedAccount(formData.fromAccountId)
    if (parseFloat(fromAccount?.balance || 0) < parseFloat(formData.amount)) {
      setError(t('validation.insufficientFunds'))
      return
    }

    setShowConfirmation(true)
  }

  // Execute the transfer
  const handleConfirmTransfer = async () => {
    setSubmitting(true)
    setShowConfirmation(false)

    try {
      let res

      if (transferMethod === 'internal') {
        res = await TransfersAPI.internal(
          formData.fromAccountId,
          formData.toAccountId,
          parseFloat(formData.amount),
          formData.description,
          formData.memo
        )
      } else {
        res = await TransfersAPI.external(
          formData.fromAccountId,
          formData.recipientId,
          parseFloat(formData.amount),
          transferMethod,
          formData.description,
          formData.memo,
          formData.scheduledDate || null
        )
      }

      if (res.success) {
        // Prepare receipt data
        const fromAccount = getSelectedAccount(formData.fromAccountId)
        const toAccount = transferMethod === 'internal' ? getSelectedAccount(formData.toAccountId) : null
        const recipient = transferMethod !== 'internal' ? recipients.find(r => r.id === formData.recipientId) : null

        setReceiptData({
          transactionId: res.data?.id || res.data?.transfer?.id || `TXN${Date.now()}`,
          amount: parseFloat(formData.amount),
          fromAccount: {
            name: fromAccount?.account_name || fromAccount?.type_name,
            number: fromAccount?.account_number
          },
          toAccount: transferMethod === 'internal' ? {
            name: toAccount?.account_name || toAccount?.type_name,
            number: toAccount?.account_number
          } : null,
          recipient: recipient ? {
            name: recipient.recipient_name,
            details: recipient.bank_name || recipient.email || recipient.phone
          } : null,
          transferType: transferMethod,
          description: formData.description,
          memo: formData.memo,
          fee: transferMethod === 'wire' ? 25 : 0,
          date: new Date(),
          status: 'completed'
        })

        setShowReceipt(true)
        fetchData()
      } else {
        setError(res.message || t('errors.transferFailed'))
      }
    } catch (err) {
      console.error('Transfer error:', err)
      setError(err.message || t('errors.transferFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  // Reset form after viewing receipt
  const handleCloseReceipt = () => {
    setShowReceipt(false)
    setReceiptData(null)
    setFormData({
      fromAccountId: accounts[0]?.id || '',
      toAccountId: '',
      recipientId: '',
      amount: '',
      description: '',
      memo: '',
      scheduledDate: ''
    })
    setTransferMethod(null)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getSelectedAccount = (id) => accounts.find(a => a.id === id)

  const handleViewTransferDetail = (transfer) => {
    setSelectedTransfer(transfer)
    setShowTransferDetail(true)
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }

  const formatDateTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderNewTransfer = () => {
    if (!transferMethod) {
      const internalMethods = transferMethods.filter(m => m.category === 'internal')
      const externalMethods = transferMethods.filter(m => m.category === 'external')

      return (
        <div>
          {/* Internal Transfers Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <h2 className="text-sm font-semibold text-slate-900">{t('sections.internal')}</h2>
              <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-full">{t('sections.available')}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {internalMethods.map((method, index) => {
                const IconComponent = method.icon
                return (
                  <motion.button
                    key={method.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setTransferMethod(method.id)}
                    className="p-6 bg-white border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all text-left group"
                  >
                    <div className={`w-12 h-12 rounded-lg ${method.color} flex items-center justify-center mb-4`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{method.name}</h3>
                    <p className="text-xs text-slate-500 mb-4">{method.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <div>
                        <span className="text-slate-500">{t('methods.fee')}: </span>
                        <span className="font-semibold text-emerald-600">{method.fee}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('methods.time')}: </span>
                        <span className="font-semibold text-slate-900">{method.time}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4 text-blue-600 group-hover:text-blue-700">
                      <span className="text-sm font-medium">{t('methods.select')}</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </div>

          {/* External Transfers Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
              <h2 className="text-sm font-semibold text-slate-900">{t('sections.external')}</h2>
              <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-0.5 rounded-full">{t('sections.comingSoon')}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {externalMethods.map((method, index) => {
                const IconComponent = method.icon
                return (
                  <motion.div
                    key={method.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (index + 1) * 0.1 }}
                    className="p-5 bg-slate-50 border-2 border-slate-200 rounded-xl text-left relative overflow-hidden opacity-60"
                  >
                    {/* Coming Soon Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="text-[10px] font-bold text-white bg-slate-400 px-2 py-1 rounded-full uppercase tracking-wide">
                        {t('sections.comingSoon')}
                      </span>
                    </div>

                    <div className={`w-10 h-10 rounded-lg ${method.color} flex items-center justify-center mb-3`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-600 mb-1">{method.name}</h3>
                    <p className="text-xs text-slate-400 mb-3">{method.description}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div>
                        <span>{t('methods.fee')}: </span>
                        <span className="font-medium">{method.fee}</span>
                      </div>
                      <div>
                        <span>{t('methods.time')}: </span>
                        <span className="font-medium">{method.time}</span>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">{t('externalComingSoon.title')}</h4>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    {t('externalComingSoon.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-sm font-semibold text-slate-900">
            {transferMethods.find(m => m.id === transferMethod)?.name}
          </h2>
          <button
            onClick={() => setTransferMethod(null)}
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {t('actions.changeMethod')}
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <p className="text-emerald-700 text-sm font-medium">{success}</p>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4 text-emerald-600" />
            </button>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700 text-sm font-medium">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4 text-red-600" />
            </button>
          </motion.div>
        )}

        <div className="space-y-6">
          {/* From Account */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.fromAccount')}</label>
            <select
              value={formData.fromAccountId}
              onChange={(e) => updateFormData('fromAccountId', e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            >
              <option value="">{t('form.selectAccount')}</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name || account.type_name} (••{account.account_number?.slice(-4)}) - ${parseFloat(account.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </option>
              ))}
            </select>
          </div>

          {/* Internal Transfer - To Account */}
          {transferMethod === 'internal' && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.toAccount')}</label>
              <select
                value={formData.toAccountId}
                onChange={(e) => updateFormData('toAccountId', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
              >
                <option value="">{t('form.selectDestination')}</option>
                {accounts.filter(acc => acc.id !== formData.fromAccountId).map(account => (
                  <option key={account.id} value={account.id}>
                    {account.account_name || account.type_name} (••{account.account_number?.slice(-4)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* External Transfer - Recipient */}
          {transferMethod !== 'internal' && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.recipient')}</label>
              <select
                value={formData.recipientId}
                onChange={(e) => updateFormData('recipientId', e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
              >
                <option value="">{t('form.selectRecipient')}</option>
                {recipients.filter(r => r.transfer_type === transferMethod || transferMethod === 'ach').map(recipient => (
                  <option key={recipient.id} value={recipient.id}>
                    {recipient.recipient_name} - {recipient.bank_name || recipient.email || recipient.phone}
                  </option>
                ))}
              </select>
              {recipients.filter(r => r.transfer_type === transferMethod).length === 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  {t('validation.noRecipientsFound')}
                </p>
              )}
            </div>
          )}

          {/* Wire Transfer Warning */}
          {transferMethod === 'wire' && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-orange-900 mb-1">{t('wireFee.title')}</p>
                  <p className="text-xs text-orange-700">
                    {t('wireFee.description')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.amount')}</label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => updateFormData('amount', e.target.value)}
                placeholder={t('form.amountPlaceholder')}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
              />
            </div>
            {formData.fromAccountId && formData.amount && (
              <p className="text-xs text-slate-500 mt-2">
                {t('form.balanceAfterTransfer')}: ${(parseFloat(getSelectedAccount(formData.fromAccountId)?.balance || 0) - parseFloat(formData.amount || 0)).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.description')}</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              placeholder={t('form.descriptionPlaceholder')}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>

          {/* Memo */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.memo')}</label>
            <input
              type="text"
              value={formData.memo}
              onChange={(e) => updateFormData('memo', e.target.value)}
              placeholder={t('form.memoPlaceholder')}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>

          {/* Schedule (External only) */}
          {transferMethod !== 'internal' && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.when')}</label>
                <select
                  value={formData.scheduledDate ? 'scheduled' : 'now'}
                  onChange={(e) => updateFormData('scheduledDate', e.target.value === 'now' ? '' : new Date().toISOString().split('T')[0])}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                >
                  <option value="now">{t('form.sendNow')}</option>
                  <option value="scheduled">{t('form.scheduleLater')}</option>
                </select>
              </div>

              {formData.scheduledDate && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2">{t('form.transferDate')}</label>
                  <input
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => updateFormData('scheduledDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
              )}
            </>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-6">
            <button
              onClick={() => setTransferMethod(null)}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all text-sm font-medium disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>
            <button
              onClick={handleReviewTransfer}
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('actions.processing')}
                </>
              ) : (
                <>
                  {t('actions.reviewTransfer')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading && accounts.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">{t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{t('title')}</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">{t('subtitle')}</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 bg-slate-100 rounded-lg p-1 mb-6 md:mb-8 overflow-x-auto">
          {['new', 'recipients', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 md:px-6 py-2.5 rounded-md transition-colors capitalize text-sm font-medium whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t(`tabs.${tab}`)}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6">
              {activeTab === 'new' && renderNewTransfer()}

              {activeTab === 'recipients' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-semibold text-slate-900">{t('recipients.saved')}</h2>
                      <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-0.5 rounded-full">{t('sections.comingSoon')}</span>
                    </div>
                  </div>

                  {/* Coming Soon State */}
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-10 h-10 text-slate-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('recipients.comingSoon.title')}</h3>
                    <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6">
                      {t('recipients.comingSoon.description')}
                    </p>

                    {/* Feature Preview */}
                    <div className="max-w-md mx-auto">
                      <div className="bg-slate-50 rounded-xl p-4 text-left">
                        <h4 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wide">{t('recipients.features.title')}</h4>
                        <ul className="space-y-2">
                          <li className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                            {t('recipients.features.saveAccounts')}
                          </li>
                          <li className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                            {t('recipients.features.storeWireDetails')}
                          </li>
                          <li className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                            {t('recipients.features.addZelleContacts')}
                          </li>
                          <li className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                            {t('recipients.features.quickRepeat')}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-sm font-semibold text-slate-900">{t('history.title')}</h2>
                    <p className="text-xs text-slate-500">{t('history.transferCount', { count: transfers.length })}</p>
                  </div>

                  {transfers.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <History className="w-8 h-8 text-slate-400" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('history.noHistory')}</h3>
                      <p className="text-xs text-slate-500 mb-4">{t('history.noHistoryDescription')}</p>
                      <button
                        onClick={() => setActiveTab('new')}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        {t('history.makeFirstTransfer')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transfers.map((transfer, index) => (
                        <motion.button
                          key={transfer.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleViewTransferDetail(transfer)}
                          className="w-full p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all text-left group"
                        >
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              transfer.transfer_type === 'internal'
                                ? 'bg-blue-50'
                                : transfer.transfer_type === 'ach'
                                ? 'bg-purple-50'
                                : transfer.transfer_type === 'wire'
                                ? 'bg-orange-50'
                                : 'bg-green-50'
                            }`}>
                              {transfer.transfer_type === 'internal' ? (
                                <ArrowUpRight className={`w-5 h-5 ${
                                  transfer.transfer_type === 'internal' ? 'text-blue-600' : 'text-purple-600'
                                }`} />
                              ) : transfer.transfer_type === 'wire' ? (
                                <Globe className="w-5 h-5 text-orange-600" />
                              ) : (
                                <Building className="w-5 h-5 text-purple-600" />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3 mb-1">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 truncate">
                                    {transfer.recipient_name || transfer.to_account_name || t('methods.internal.title')}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate">
                                    {transfer.description || transferMethods.find(m => m.id === transfer.transfer_type)?.name || t('methods.internal.title')}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-sm font-bold text-slate-900">
                                    -${parseFloat(transfer.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                  </p>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                                    transfer.status === 'completed'
                                      ? 'bg-emerald-50 text-emerald-600'
                                      : transfer.status === 'pending'
                                      ? 'bg-orange-50 text-orange-600'
                                      : transfer.status === 'cancelled'
                                      ? 'bg-red-50 text-red-600'
                                      : transfer.status === 'processing'
                                      ? 'bg-blue-50 text-blue-600'
                                      : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {transfer.status === 'completed' && <Check className="w-2.5 h-2.5" />}
                                    {t(`status.${transfer.status}`)}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-slate-400">{formatDate(transfer.created_at)}</p>
                                <div className="flex items-center gap-1 text-slate-400 group-hover:text-blue-600 transition-colors">
                                  <span className="text-xs font-medium">{t('history.viewDetails')}</span>
                                  <ChevronRight className="w-3 h-3" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.button>
                      ))}

                      {/* Load More hint */}
                      {transfers.length >= 10 && (
                        <div className="text-center pt-4">
                          <p className="text-xs text-slate-500">{t('history.showingLast', { count: 10 })}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">{t('sidebar.thisMonth')}</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t('sidebar.totalSent')}</p>
                  <p className="text-xl font-bold text-slate-900">
                    ${transfers.reduce((sum, tr) => sum + parseFloat(tr.amount || 0), 0).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sidebar.transfersMade')}</p>
                  <p className="text-xl font-bold text-slate-900">{transfers.length}</p>
                </div>
              </div>
            </div>

            {/* Transfer Limits */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('limits.title')}</h3>
                  <p className="text-xs text-blue-700 leading-relaxed mb-3">
                    {t('limits.daily')}: $10,000
                    <br />
                    {t('limits.monthly')}: $50,000
                  </p>
                  <button className="text-xs font-medium text-blue-700 hover:text-blue-800">
                    {t('limits.requestIncrease')}
                  </button>
                </div>
              </div>
            </div>

            {/* Security Note */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('sidebar.secureTransfers')}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {t('sidebar.securityNote')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowConfirmation(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
                <h2 className="text-xl font-bold text-slate-900">{t('confirmation.title')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('review.pleaseReview')}</p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* Amount */}
                <div className="text-center py-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">{t('review.amount')}</p>
                  <p className="text-4xl font-bold text-slate-900">
                    ${parseFloat(formData.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  {transferMethod === 'wire' && (
                    <p className="text-xs text-orange-600 mt-2">+ $25.00 wire transfer fee</p>
                  )}
                </div>

                {/* From Account */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <ArrowRight className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('review.from')}</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {getSelectedAccount(formData.fromAccountId)?.account_name || getSelectedAccount(formData.fromAccountId)?.type_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        ••{getSelectedAccount(formData.fromAccountId)?.account_number?.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-slate-600">
                    {t('review.balance')}: ${parseFloat(getSelectedAccount(formData.fromAccountId)?.balance || 0).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                  </p>
                </div>

                {/* To Account / Recipient */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('review.to')}</p>
                      {transferMethod === 'internal' ? (
                        <>
                          <p className="text-sm font-semibold text-slate-900">
                            {getSelectedAccount(formData.toAccountId)?.account_name || getSelectedAccount(formData.toAccountId)?.type_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            ••{getSelectedAccount(formData.toAccountId)?.account_number?.slice(-4)}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-slate-900">
                            {recipients.find(r => r.id === formData.recipientId)?.recipient_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {recipients.find(r => r.id === formData.recipientId)?.bank_name ||
                             recipients.find(r => r.id === formData.recipientId)?.email ||
                             recipients.find(r => r.id === formData.recipientId)?.phone}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Transfer Details */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{t('review.transferType')}</span>
                    <span className="font-medium text-slate-900">
                      {transferMethods.find(m => m.id === transferMethod)?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{t('review.estimatedArrival')}</span>
                    <span className="font-medium text-slate-900">
                      {transferMethods.find(m => m.id === transferMethod)?.time}
                    </span>
                  </div>
                  {formData.description && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{t('review.description')}</span>
                      <span className="font-medium text-slate-900">{formData.description}</span>
                    </div>
                  )}
                </div>

                {/* Warning */}
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-700">
                      {t('review.verifyDetails')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 py-3 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  {t('actions.cancel')}
                </button>
                <button
                  onClick={handleConfirmTransfer}
                  disabled={submitting}
                  className="flex-1 py-3 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('actions.processing')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('actions.confirmSend')}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Receipt Modal */}
      <AnimatePresence>
        {showReceipt && receiptData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            >
              {/* Success Header */}
              <div className="px-6 py-8 bg-gradient-to-br from-emerald-500 to-emerald-600 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white">{t('confirmation.successTitle')}</h2>
                <p className="text-emerald-100 mt-1">{t('confirmation.subtitle')}</p>
              </div>

              {/* Receipt Details */}
              <div className="p-6 space-y-4">
                {/* Amount */}
                <div className="text-center py-4 border-b border-dashed border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">{t('confirmation.amountTransferred')}</p>
                  <p className="text-3xl font-bold text-slate-900">
                    ${receiptData.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  {receiptData.fee > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Fee: ${receiptData.fee.toFixed(2)}</p>
                  )}
                </div>

                {/* Transaction Details */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('confirmation.transferId')}</span>
                    <span className="text-sm font-mono text-slate-900">{receiptData.transactionId?.slice(0, 12)}...</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('confirmation.dateTime')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {receiptData.date.toLocaleDateString(i18n.language, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('review.from')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {receiptData.fromAccount.name} (••{receiptData.fromAccount.number?.slice(-4)})
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('confirmation.to')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {receiptData.toAccount
                        ? `${receiptData.toAccount.name} (••${receiptData.toAccount.number?.slice(-4)})`
                        : receiptData.recipient?.name
                      }
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('review.transferType')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {transferMethods.find(m => m.id === receiptData.transferType)?.name}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('detail.transactionId')}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                      <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                      {t('status.completed')}
                    </span>
                  </div>

                  {receiptData.description && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{t('review.description')}</span>
                      <span className="text-sm font-medium text-slate-900">{receiptData.description}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => {
                    // Copy receipt to clipboard
                    const receiptText = `Transfer Receipt\n\nAmount: $${receiptData.amount.toFixed(2)}\nTransaction ID: ${receiptData.transactionId}\nDate: ${receiptData.date.toLocaleString()}\nFrom: ${receiptData.fromAccount.name}\nTo: ${receiptData.toAccount?.name || receiptData.recipient?.name}\nStatus: Completed`
                    navigator.clipboard.writeText(receiptText)
                  }}
                  className="flex-1 py-3 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('confirmation.saveReceipt')}
                </button>
                <button
                  onClick={handleCloseReceipt}
                  className="flex-1 py-3 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  {t('confirmation.done')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transfer Detail Modal */}
      <AnimatePresence>
        {showTransferDetail && selectedTransfer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTransferDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{t('detail.title')}</h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {formatDateTime(selectedTransfer.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTransferDetail(false)}
                    className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Amount Section */}
                <div className="text-center py-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">{t('review.amount')}</p>
                  <p className="text-3xl font-bold text-slate-900">
                    ${parseFloat(selectedTransfer.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  {selectedTransfer.fee && parseFloat(selectedTransfer.fee) > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Fee: ${parseFloat(selectedTransfer.fee).toFixed(2)}
                    </p>
                  )}
                </div>

                {/* Status Badge */}
                <div className="flex items-center justify-center">
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                    selectedTransfer.status === 'completed'
                      ? 'bg-emerald-50 text-emerald-700'
                      : selectedTransfer.status === 'pending'
                      ? 'bg-orange-50 text-orange-700'
                      : selectedTransfer.status === 'cancelled'
                      ? 'bg-red-50 text-red-700'
                      : selectedTransfer.status === 'processing'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedTransfer.status === 'completed' && <CheckCircle className="w-4 h-4" />}
                    {selectedTransfer.status === 'pending' && <Clock className="w-4 h-4" />}
                    {selectedTransfer.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {selectedTransfer.status === 'cancelled' && <X className="w-4 h-4" />}
                    {selectedTransfer.status?.charAt(0).toUpperCase() + selectedTransfer.status?.slice(1)}
                  </span>
                </div>

                {/* Transfer Flow */}
                <div className="space-y-3">
                  {/* From */}
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-red-600 font-medium">{t('review.from')}</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedTransfer.from_account_name || t('common:account')}
                      </p>
                      {selectedTransfer.from_account_number && (
                        <p className="text-xs text-slate-500">
                          ••{selectedTransfer.from_account_number.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  {/* To */}
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-emerald-600 font-medium">{t('review.to')}</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedTransfer.recipient_name || selectedTransfer.to_account_name || t('form.recipient')}
                      </p>
                      {selectedTransfer.to_account_number && (
                        <p className="text-xs text-slate-500">
                          ••{selectedTransfer.to_account_number.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  {/* Transaction ID */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('detail.transactionId')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-slate-900">
                        {selectedTransfer.id?.slice(0, 8)}...
                      </span>
                      <button
                        onClick={() => copyToClipboard(selectedTransfer.id, 'Transaction ID')}
                        className="p-1 hover:bg-slate-100 rounded transition-colors"
                        title={t('confirmation.copyDetails')}
                      >
                        <Copy className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>
                  </div>

                  {/* Transfer Type */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('review.transferType')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {transferMethods.find(m => m.id === selectedTransfer.transfer_type)?.name ||
                       selectedTransfer.transfer_type?.charAt(0).toUpperCase() + selectedTransfer.transfer_type?.slice(1) ||
                       t('methods.internal.title')}
                    </span>
                  </div>

                  {/* Description */}
                  {selectedTransfer.description && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{t('review.description')}</span>
                      <span className="text-sm font-medium text-slate-900 text-right max-w-[60%] truncate">
                        {selectedTransfer.description}
                      </span>
                    </div>
                  )}

                  {/* Memo */}
                  {selectedTransfer.memo && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{t('form.memo')}</span>
                      <span className="text-sm font-medium text-slate-900 text-right max-w-[60%] truncate">
                        {selectedTransfer.memo}
                      </span>
                    </div>
                  )}

                  {/* Created Date */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{t('detail.created')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {formatDateTime(selectedTransfer.created_at)}
                    </span>
                  </div>

                  {/* Completed Date */}
                  {selectedTransfer.completed_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{t('detail.completed')}</span>
                      <span className="text-sm font-medium text-slate-900">
                        {formatDateTime(selectedTransfer.completed_at)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => {
                    const receiptText = `Transfer Receipt\n\nAmount: $${parseFloat(selectedTransfer.amount).toFixed(2)}\nTransaction ID: ${selectedTransfer.id}\nDate: ${formatDateTime(selectedTransfer.created_at)}\nFrom: ${selectedTransfer.from_account_name || 'N/A'}\nTo: ${selectedTransfer.recipient_name || selectedTransfer.to_account_name || 'N/A'}\nType: ${selectedTransfer.transfer_type}\nStatus: ${selectedTransfer.status}`
                    navigator.clipboard.writeText(receiptText)
                  }}
                  className="flex-1 py-3 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {t('confirmation.copyDetails')}
                </button>
                <button
                  onClick={() => setShowTransferDetail(false)}
                  className="flex-1 py-3 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  {t('common:close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
